<template>
  <div class="course-resource">
    <!-- 根目录且列表为空：按产品稿展示搜索 + 上传 + 空状态插画（与实训项目列表空态一致） -->
    <template v-if="showRootEmptyResourceState">
      <div class="filters filters--root-empty">
        <ul-input
          class="input-search input-search--root-empty"
          v-model="keyword"
          clearable
          search
          suffix-icon="icon-quanshipei-sousuo"
          @keydown.native.enter="searchResource"
          @clear="searchResource"
          @search="searchResource"
          placeholder="输入关键字搜索"
        ></ul-input>
        <ul-button type="primary" size="small" class="button-right" ref="uploadButton">上传文件</ul-button>
      </div>
      <div class="resource-root-empty">
        <img :src="emptyResourceImg" alt="">
        <p>暂无实验资源</p>
      </div>
    </template>
    <template v-else>
    <!-- 仅在子目录展示面包屑；批量删除条不占本行，改与筛选行同一槽位切换 -->
    <div class="filters" v-if="isInFolder">
      <div class="folder-path">
        <span class="back-link" @click="backToRoot">
          <i class="iconfont icon-shangyige"></i> 返回
        </span>
        <span class="divider"></span>
        <span class="breadcrumb-item breadcrumb-root" @click="backToRoot">全部资源</span>
        <template v-for="item in folderPath">
          <i class="iconfont icon-xialacaozuo" :key="'path-icon-' + item.id"></i>
          <span class="breadcrumb-item breadcrumb-current" :key="item.id" @click="enterFolder(item)">{{ item.resourceName }}</span>
        </template>
      </div>
    </div>
    <!-- 未勾选：筛选行（全部类型 + 搜索）；已勾选：同网格槽位替换为批量删除条，右侧仍保留上传与操作列对齐 -->
    <div v-if="!showResourceBulkBar" class="folder-filters">
      <div class="folder-filters-left folder-filters__cell folder-filters__cell--filters">
        <ul-select class="select-type" v-model="selectedResourceType" @change="searchResource">
          <ul-option
            v-for="item in resourceTypeList"
            :key="item.id"
            :label="item.name"
            :value="item.id">
          </ul-option>
        </ul-select>
        <ul-input
          class="input-search"
          v-model="keyword"
          clearable
          search
          suffix-icon="icon-quanshipei-sousuo"
          @keydown.native.enter="searchResource"
          @clear="searchResource"
          @search="searchResource"
          placeholder="输入关键字搜索"
        ></ul-input>
      </div>
      <div class="folder-filters__cell folder-filters__cell--creator-gap" aria-hidden="true"></div>
      <div class="folder-filters__cell folder-filters__cell--time-gap" aria-hidden="true"></div>
      <div class="folder-filters__cell folder-filters__cell--action">
        <ul-button type="primary" size="small" class="button-right" ref="uploadButton">上传文件</ul-button>
      </div>
    </div>
    <div v-else class="folder-filters folder-filters--bulk">
      <div class="folder-filters-left folder-filters__cell folder-filters__cell--filters">
        <div class="resource-bulk-bar">
          <button type="button" class="bulk-delete-text" @click="deleteSelectedResources">删除</button>
          <span class="selected-count">已选{{ selectedCount }}项</span>
        </div>
      </div>
      <div class="folder-filters__cell folder-filters__cell--creator-gap" aria-hidden="true"></div>
      <div class="folder-filters__cell folder-filters__cell--time-gap" aria-hidden="true"></div>
      <div class="folder-filters__cell folder-filters__cell--action">
        <ul-button type="primary" size="small" class="button-right" ref="uploadButton">上传文件</ul-button>
      </div>
    </div>
    <div class="resource-table">
      <div class="resource-row resource-header">
        <div class="resource-cell checkbox-cell">
          <input type="checkbox" :checked="isAllSelected" @change="toggleAllSelection">
        </div>
        <div class="resource-cell name-cell">
          <span
            class="resource-header-sort-trigger"
            role="button"
            tabindex="0"
            @click.prevent="toggleResourceListSort('title')"
            @keydown.enter.prevent="toggleResourceListSort('title')"
            @keydown.space.prevent="toggleResourceListSort('title')"
          >
            <span>资源名称</span>
            <span class="total-text">总计{{ totalNum }}个</span>
            <span class="sort-pair" :class="sortPairModifiers('title')" aria-hidden="true"></span>
          </span>
        </div>
        <div class="resource-cell creator-cell">
          <span
            class="resource-header-sort-trigger"
            role="button"
            tabindex="0"
            @click.prevent="toggleResourceListSort('creatorName')"
            @keydown.enter.prevent="toggleResourceListSort('creatorName')"
            @keydown.space.prevent="toggleResourceListSort('creatorName')"
          >
            <span>创建人</span>
            <span class="sort-pair" :class="sortPairModifiers('creatorName')" aria-hidden="true"></span>
          </span>
        </div>
        <div class="resource-cell time-cell">
          <span
            class="resource-header-sort-trigger"
            role="button"
            tabindex="0"
            @click.prevent="toggleResourceListSort('createTime')"
            @keydown.enter.prevent="toggleResourceListSort('createTime')"
            @keydown.space.prevent="toggleResourceListSort('createTime')"
          >
            <span>创建时间</span>
            <span class="sort-pair" :class="sortPairModifiers('createTime')" aria-hidden="true"></span>
          </span>
        </div>
        <div class="resource-cell action-cell">操作</div>
      </div>
      <div class="resource-row" v-for="item in resourceList" :key="item.id" :class="{ 'is-row-selected': isResourceSelected(item) }">
        <div class="resource-cell checkbox-cell">
          <input type="checkbox" :checked="isResourceSelected(item)" @change="toggleResourceSelection(item)">
        </div>
        <div class="resource-cell name-cell">
          <div class="resource-name" @click="viewResource(item)">
            <i class="iconfont resource-icon" :class="[getResourceIcon(item), getResourceIconColorClass(item)]"></i>
            <span :title="item.resourceName">{{ item.resourceName }}</span>
          </div>
        </div>
        <div class="resource-cell creator-cell">{{ item.creatorName }}</div>
        <div class="resource-cell time-cell">{{ formatResourceTime(item.createTime) }}</div>
        <div class="resource-cell action-cell">
          <ul-dropdown @command="handleCommand($event, item)">
            <ul-button class="ul-dropdown-link" size="small" hollow>
              <span class="button-text">{{ getActionText(item) }}</span>
              <i class="iconfont icon-xialacaozuo"></i>
            </ul-button>
            <ul-dropdown-menu slot="dropdown" class="resource-menu">
              <ul-dropdown-item command="view" v-if="item.resourceType === 'file'">查看详情</ul-dropdown-item>
              <ul-dropdown-item command="rename">重命名</ul-dropdown-item>
              <ul-dropdown-item command="delete">删除</ul-dropdown-item>
            </ul-dropdown-menu>
          </ul-dropdown>
        </div>
      </div>
    </div>
    <ul-pagination
      class="pagination"
      layout="prev, pager, next, jumper"
      prevText="上一页"
      nextText="下一页"
      :total="totalNum"
      :page-size.sync="params.ps"
      :current-page.sync="params.pn"
      @current-change="getResourceList"
    ></ul-pagination>
    </template>
    <ul-dialog
      width="420px"
      title="重命名"
      :visible.sync="renameDialogVisible"
    >
      <ul-form ref="renameForm" :model="renameForm" :rules="renameRules">
        <ul-form-item
          prop="resourceName"
          class="rename-form-item"
          :class="{ 'rename-form-item--name-taken': !!renameDuplicateNameError }"
        >
          <ul-input
            v-model="renameForm.resourceName"
            placeholder="请输入资源名称"
            @input="onRenameResourceNameInput"
          ></ul-input>
          <!-- 重名提示：固定展示在输入框下方（图2），避免走全局 Message -->
          <div v-if="renameDuplicateNameError" class="rename-duplicate-msg">
            {{ renameDuplicateNameError }}
          </div>
        </ul-form-item>
      </ul-form>
      <span slot="footer" class="dialog-footer">
        <ul-button type="primary" @click="submitRename">确定</ul-button>
        <ul-button plain @click="renameDialogVisible = false">取消</ul-button>
      </span>
    </ul-dialog>
    <ResourcePreview
      :resource="currentPreviewResource"
      v-if="isResourcePreviewVisible"
      @hide="isResourcePreviewVisible = false"
    ></ResourcePreview>
    <transition name="mask-fade">
      <div class="document-preview-wrapper" v-if="isDocumentPreviewVisible">
        <div class="document-preview-header">
          <h4>{{ currentDocumentPreviewResource.fileName }}</h4>
          <div class="document-preview-actions">
            <a
              v-if="!documentDownloadUsesObsToken"
              class="preview-button"
              :href="currentDocumentOriginalPath"
              target="_blank"
              download
            >
              下载（{{ documentDownloadSizeDisplay }}）
            </a>
            <button
              v-else
              type="button"
              class="preview-button"
              @click="onDocumentFullscreenDownloadClick"
            >
              下载（{{ documentDownloadSizeDisplay }}）
            </button>
            <button class="preview-button" type="button" @click="isDocumentPreviewVisible = false">关闭</button>
          </div>
        </div>
        <div class="document-preview-body">
          <iframe :src="currentDocumentPreviewPath"></iframe>
        </div>
      </div>
    </transition>
    <!-- 上传过程中全屏等待提示（OBS 直传完成并保存/关联课程资源前） -->
    <transition name="upload-wait-fade">
      <div v-if="showUploadWaitOverlay" class="upload-wait-overlay">
        <div class="upload-wait-panel">
          <div class="upload-wait-spinner" aria-hidden="true"></div>
          <p class="upload-wait-text">文件上传中，请稍候…</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Obs from "@/plugins/ulearning-obs.js";
import {
  deleteExperimentSyncedCourseContent,
  editCourseContent,
  editExperimentSyncedCourseContent,
  relateCourseContent,
  searchCourseContentResources,
  saveCourseContent,
  getCourseContentDetail,
  getObsDownloadToken
} from '@/api/courseApi/virtual.js'
import { API_HOST, RESOURCE_SERVER_HOST } from '@/config'
import { fileIcon, formatSize, getFullPath, setObsConfig, extractObsRelativePath } from '@/utils/file.js'
import { formatDate } from '@/utils'
import { mapGetters } from "vuex";
import ResourcePreview from "pc/components/resourcePreview.vue";
import emptyResourceImg from '@/assets/images/empty1.png'

export default {
  name: 'course-resource',
  components: {
    ResourcePreview
  },
  computed: {
    ...mapGetters({ user: 'user/user', token: "user/token" }),
    selectedCount() {
      return this.selectedResourceIds.length
    },
    isInFolder() {
      return this.params.parentId !== 0
    },
    /** 仅在「全部资源」根下、未做类型/关键字筛选、且确实无任何资源时展示整页空态插画；筛选/搜索无数据时仍保留表头与筛选条，仅列表为空。列表请求过程中不展示空态插画，避免「无数据筛选 → 全部类型」切换时闪屏 */
    showRootEmptyResourceState() {
      if (this.resourceListLoading) {
        return false
      }
      const hasKeyword = String(this.keyword || '').trim().length > 0
      const hasTypeFilter = this.selectedResourceType !== '' && this.selectedResourceType !== null && this.selectedResourceType !== undefined
      if (hasKeyword || hasTypeFilter) {
        return false
      }
      return (
        !this.isInFolder &&
        this.totalNum === 0 &&
        this.resourceList.length === 0
      )
    },
    /** 列表有数据且至少勾选一项时展示「删除 + 已选N项」（图2），否则不展示（图1） */
    showResourceBulkBar() {
      if (this.showRootEmptyResourceState) {
        return false
      }
      return this.resourceList.length > 0 && this.selectedCount > 0
    },
    isAllSelected() {
      return this.resourceList.length > 0 && this.selectedResourceIds.length === this.resourceList.length
    },
    currentTeacherId() {
      return this.user.teacherId || this.user.userId || this.user.id || ''
    },
    currentDocumentOriginalPath() {
      return getFullPath(this.currentDocumentPreviewResource.filePath)
    },
    currentDocumentPreviewPath() {
      if (this.isDocumentResource(this.currentDocumentPreviewResource.filePath)) {
        // 不能直接拼 PW/GetPage 的 f 参数，交给文档预览服务按 furl 生成预览页。
        let url = 'https://docs.ulearning.cn?time=1'
        if (this.currentDocumentOriginalPath.indexOf('https://') !== -1) {
          url += '&ssl=1'
        }
        return `${url}&furl=${encodeURIComponent(this.currentDocumentOriginalPath)}`
      }
      return this.currentDocumentOriginalPath
    },
    /** 文档全屏预览：详情 contentSize 自动换算 B/KB/MB/GB；缺省时占位 */
    documentDownloadSizeDisplay() {
      const raw = this.currentDocumentPreviewResource.contentSize
      if (raw !== undefined && raw !== null && raw !== '') {
        const t = formatSize(raw)
        return t || '—'
      }
      return '—'
    },
    /** 文档全屏预览是否走 OBS downloadToken（路径可解析为 resources/web/... 时） */
    documentDownloadUsesObsToken () {
      return !!extractObsRelativePath(this.currentDocumentPreviewResource.filePath)
    },
    /** 是否显示上传等待遮罩 */
    showUploadWaitOverlay() {
      return this.uploadWaitTaskCount > 0
    }
  },
  data() {
    return {
      keyword: '',
      selectedResourceType: '',
      resourceTypeList: [
        { id: '', name: '全部类型' },
        { id: 14, name: '微课' },
        { id: 1, name: '视频' },
        { id: 2, name: '音频' },
        { id: 3, name: '图片' },
        { id: 4, name: '文档' },
        { id: 7, name: '链接' },
        { id: 8, name: '图文' },
        { id: 6, name: '其他' }
      ],
      resourceList: [],
      selectedResourceIds: [],
      totalNum: 0,
      /** 资源列表请求中；为 true 时不切换到「暂无实验资源」整页空态，避免筛选条件切换时的闪屏 */
      resourceListLoading: true,
      params: {
        pn: 1,
        ps: 10,
        parentId: 0
      },
      folderPath: [],
      renameDialogVisible: false,
      currentResource: null,
      currentPreviewResource: {},
      isResourcePreviewVisible: false,
      currentDocumentPreviewResource: {},
      isDocumentPreviewVisible: false,
      renameForm: {
        resourceName: ''
      },
      // 与资源列表重复时的提示文案，由模板在输入框下方展示
      renameDuplicateNameError: '',
      renameRules: {
        resourceName: [
          { required: true, message: '请输入资源名称', trigger: 'blur' },
          { max: 100, message: '资源名称最多100字', trigger: 'blur' },
        ]
      },
      emptyResourceImg,
      /** 进行中的上传任务数（OBS + 保存接口），>0 时显示等待层，支持多文件并发 */
      uploadWaitTaskCount: 0,
      /** 表头排序：与 /course/content/order 的 orderBy 一致 — title、creatorName、createTime（desc：0 升序、1 降序） */
      listOrderBy: 'createTime',
      listDesc: 1
    }
  },
  watch: {
    // 根目录空态与普通列表共用不同「上传」按钮 DOM，切换后需重新挂载 Obs
    showRootEmptyResourceState() {
      this.$nextTick(() => {
        if (this.$refs.uploadButton && this.$refs.uploadButton.$el) {
          this.initUploader()
        }
      })
    },
    // 筛选行与批量条二选一挂载，上传按钮 DOM 会变，需在下一帧重新初始化 OBS 绑定
    showResourceBulkBar() {
      if (this.showRootEmptyResourceState) {
        return
      }
      this.$nextTick(() => {
        if (this.$refs.uploadButton && this.$refs.uploadButton.$el) {
          this.initUploader()
        }
        this.scheduleVirtualHomeSidebarSync()
      })
    }
  },
  created() {
    this.getResourceList()
  },
  mounted() {
    this.$nextTick(() => this.initUploader())
  },
  methods: {
    getResourceList(page) {
      if (page) {
        this.params.pn = page
      }
      const params = {
        courseId: this.$route.params.id,
        ocId: this.$route.params.id,
        keyword: this.keyword,
        pn: this.params.pn,
        ps: this.params.ps,
        parentId: this.params.parentId,
        teacherId: this.currentTeacherId,
        version: 2,
        orderBy: this.listOrderBy,
        desc: this.listDesc
      }
      if (this.selectedResourceType !== '') {
        params.type = this.selectedResourceType
      }
      // 表头排序依赖 /course/content/order（示例：orderBy=title|c creatorName|createTime & desc）；与关键字/目录/类型筛选共用同一列表接口
      this.resourceListLoading = true
      searchCourseContentResources(params)
        .then((res) => {
          const result = res.result || res
          const list = result.list || result.records || []
          this.resourceList = list.map((item, index) => this.formatResource(item, index))
          this.totalNum = result.total || result.totalNum || list.length
          this.selectedResourceIds = []
        })
        .catch(() => {})
        .finally(() => {
          this.resourceListLoading = false
        })
    },
    formatResource(item, index) {
      const fileUrl = item.fileUrl || item.resourceUrl || item.url || item.location
      return {
        id: item.id || item.resourceId || `resource-${index}`,
        parentId: item.parentId,
        resourceName: item.resourceName || item.fileName || item.folderName || item.title || item.name,
        // 实验资源列表接口 (/virtualReality/course/experiment/resources) 将创建人展示名置于 name；资源标题在 title，故不会影响 resourceName 的取值顺序
        creatorName: item.creatorName || item.createUserName || item.teacherName || item.teacher || item.name || '--',
        createTime: item.createTime || item.createdTime || item.createDate,
        resourceType: this.getResourceType(item, fileUrl),
        fileUrl,
        contentSize: item.contentSize || item.size,
        ocId: item.ocId,
        share: item.share,
        type: item.type
      }
    },
    initUploader() {
      const obs = new Obs(setObsConfig({
        uptokenHost: API_HOST,
        authorization: this.token,
        obsHost: RESOURCE_SERVER_HOST,
      }))
      obs.initUpBtn(this.$refs.uploadButton.$el)
      obs.onBeforeUpload = (file) => {
        // 显示整页等待层直至保存并关联成功或失败
        this.beginUploadWait()
        const tempResource = {
          id: `upload-${Date.now()}`,
          resourceName: file.name,
          creatorName: this.getCurrentUserName(),
          createTime: Date.now(),
          resourceType: 'file',
          fileUrl: '',
          percent: 0
        }
        this.resourceList.unshift(tempResource)
        file.onProgress = (percent) => {
          tempResource.percent = percent
        }
        file.onSuccess = () => {
          tempResource.fileUrl = `${file.obsHost}/${file.key}`
          tempResource.percent = 0
          this.saveUploadedResource(file, tempResource)
        }
        file.onError = () => {
          this.endUploadWait()
          this.resourceList = this.resourceList.filter(item => item.id !== tempResource.id)
          this.$message({
            message: '上传失败',
            type: 'error'
          })
        }
      }
    },
    /** 开始一项上传任务（等待层计数 +1） */
    beginUploadWait() {
      this.uploadWaitTaskCount += 1
    },
    /** 结束一项上传任务（计数 -1，不小于 0） */
    endUploadWait() {
      this.uploadWaitTaskCount = Math.max(0, this.uploadWaitTaskCount - 1)
    },
    saveUploadedResource(file, tempResource) {
      const fileUrl = `${file.obsHost}/${file.key}`
      const contentData = {
        contentSize: file.size,
        isView: 0,
        location: fileUrl,
        mimeType: this.getFileExt(file.name),
        remark2: 1,
        remark3: 0,
        status: 2,
        title: file.name,
        type: 1
      }
      saveCourseContent(contentData)
        .then((res) => {
          const contentId = this.getContentId(res)
          if (!contentId) {
            return Promise.reject(new Error('资源保存结果缺少 contentId'))
          }
          const relationData = {
            contentIds: [
              {
                id: null,
                contentId,
                localType: 0
              }
            ],
            ocId: this.$route.params.id,
            parentId: this.params.parentId
          }
          return relateCourseContent(relationData)
        })
        .then(() => {
          this.$message({
            message: '上传成功',
            type: 'success'
          })
          this.getResourceList()
        })
        .catch(() => {
          this.resourceList = this.resourceList.filter(item => item.id !== tempResource.id)
          this.$message({
            message: '上传成功，但资源保存或关联失败，请重试。',
            type: 'error'
          })
        })
        .finally(() => {
          this.endUploadWait()
        })
    },
    getContentId(res) {
      const result = res.result || res
      if (typeof result === 'number') {
        return result
      }
      if (!result) {
        return null
      }
      return result.id || result.contentId
    },
    searchResource() {
      this.keyword = this.keyword.trim()
      this.params.pn = 1
      this.getResourceList()
    },
    /** 切换表头排序：再次点击同一列时 desc 在 0/1 间切换 */
    toggleResourceListSort(apiOrderBy) {
      if (this.listOrderBy === apiOrderBy) {
        this.listDesc = this.listDesc === 1 ? 0 : 1
      } else {
        this.listOrderBy = apiOrderBy
        // 创建时间默认新在前(desc=1)；名称、创建人与接口示例一致首点为升序(desc=0)
        this.listDesc = apiOrderBy === 'createTime' ? 1 : 0
      }
      this.params.pn = 1
      this.getResourceList()
    },
    /** 排序三角标：当前列高亮朝上/朝下 */
    sortPairModifiers(apiOrderBy) {
      const active = this.listOrderBy === apiOrderBy
      return {
        'sort-pair--asc': active && this.listDesc === 0,
        'sort-pair--desc': active && this.listDesc === 1
      }
    },
    getResourceType(item, fileUrl) {
      // 后端可能返回 type/resourceType/isFolder，这里统一转换为页面使用的类型。
      if (item.resourceType === 'folder' || item.type === 'folder' || item.isFolder === 1 || item.contentId === 0) {
        return 'folder'
      }
      return fileUrl ? 'file' : 'folder'
    },
    getResourceIcon(item) {
      if (item.resourceType === 'folder') {
        return 'icon-wenjianjia'
      }
      return fileIcon(item.fileUrl || item.resourceName)
    },
    getResourceIconColorClass(item) {
      if (item.resourceType === 'folder') {
        return 'resource-icon-folder'
      }
      const ext = this.getFileExt(item.fileUrl || item.resourceName || '')
      if (['pdf'].includes(ext)) {
        return 'resource-icon-pdf'
      }
      if (['xls', 'xlsx'].includes(ext)) {
        return 'resource-icon-excel'
      }
      if (['doc', 'docx'].includes(ext)) {
        return 'resource-icon-word'
      }
      if (['ppt', 'pptx'].includes(ext)) {
        return 'resource-icon-ppt'
      }
      if (['mp4', 'avi', 'wmv', 'mov', 'flv'].includes(ext)) {
        return 'resource-icon-video'
      }
      return 'resource-icon-other'
    },
    formatResourceTime(time) {
      if (!time) {
        return '--'
      }
      const timestamp = Number(time)
      if (!Number.isNaN(timestamp)) {
        // 与实验资源列表 UI 稿一致：创建时间为 YYYY-MM-DD
        return formatDate(timestamp < 1000000000000 ? timestamp * 1000 : timestamp, 'yyyy-MM-dd')
      }
      return time
    },
    getCurrentUserName() {
      return this.user.realName || this.user.name || this.user.userName || '--'
    },
    handleCommand(command, item) {
      switch (command) {
        case 'view':
          this.viewResource(item)
          break;
        case 'rename':
          this.openRenameDialog(item)
          break;
        case 'delete':
          this.deleteResource(item)
          break;
        default:
          break;
      }
    },
    getActionText(item) {
      return item.resourceType === 'folder' ? '重命名' : '查看详情'
    },
    isResourceSelected(item) {
      return this.selectedResourceIds.includes(item.id)
    },
    toggleResourceSelection(item) {
      if (this.isResourceSelected(item)) {
        this.selectedResourceIds = this.selectedResourceIds.filter(id => id !== item.id)
      } else {
        this.selectedResourceIds.push(item.id)
      }
    },
    toggleAllSelection(event) {
      if (event.target.checked) {
        this.selectedResourceIds = this.resourceList.map(item => item.id)
      } else {
        this.selectedResourceIds = []
      }
    },
    /** 将勾选 id 转为后端删除接口所需的整数列表（排除上传中等非数字 id） */
    normalizeContentIdsForDelete(rawIds) {
      const ids = []
      for (const raw of rawIds) {
        const n = Number(raw)
        if (Number.isInteger(n) && n > 0) {
          ids.push(n)
        }
      }
      return ids
    },
    deleteSelectedResources() {
      if (this.selectedResourceIds.length === 0) {
        return
      }
      const idsToDelete = this.normalizeContentIdsForDelete(this.selectedResourceIds)
      if (idsToDelete.length === 0) {
        this.$message({ message: '当前选中项无法删除，请刷新列表后重试', type: 'warning' })
        return
      }
      this.$confirm('确定删除选中的资源吗？删除后无法恢复。', '删除')
        .then(() => {
          return this.deleteExperimentSyncedResources(idsToDelete)
        })
        .then(() => {
          this.$message({ message: '删除成功', type: 'success' })
          this.getResourceList()
        })
        .catch((reason) => {
          // 用户关闭确认框：不当作接口失败提示
          if (reason === 'cancel' || reason === 'close') {
            return
          }
          const apiMsg = this.extractCourseApiErrorMessage(reason)
          if (apiMsg) {
            this.$message({ message: apiMsg, type: 'error' })
          }
        })
    },
    /** 实验同步目录内删除：文件夹走新接口以同步删除 AI 实验附件；文件同样走新接口 */
    deleteExperimentSyncedResources(idsToDelete) {
      return deleteExperimentSyncedCourseContent(idsToDelete)
    },
    viewResource(item) {
      if (item.resourceType === 'folder') {
        this.enterFolder(item)
        return
      }
      if (!(item.resourceType === 'file' && item.fileUrl)) {
        return
      }

      /** 详情成功后合并展示名、存储地址与 contentSize（与 GET /course/content/:id 一致） */
      const openFromMergedDetail = (detail) => {
        const d = detail && typeof detail === 'object' ? detail : {}
        const location = d.location || item.fileUrl
        const displayName =
          d.title ||
          d.remark1 ||
          d.name ||
          item.resourceName
        const contentSize =
          d.contentSize !== undefined && d.contentSize !== null
            ? d.contentSize
            : item.contentSize

        const stub = { fileUrl: location, resourceName: displayName }
        if (this.isEmbeddedDocument(stub)) {
          this.currentDocumentPreviewResource = {
            fileName: displayName,
            filePath: location,
            contentSize
          }
          this.isDocumentPreviewVisible = true
          return
        }
        this.currentPreviewResource = {
          fileName: displayName,
          filePath: location,
          contentSize
        }
        this.isResourcePreviewVisible = true
      }

      const resourcePk = item.id
      // 上传占位行 id 形如 upload-*，无后端详情可拉取
      const isTempUploadRow =
        typeof resourcePk === 'string' && resourcePk.indexOf('upload-') === 0
      if (
        resourcePk === undefined ||
        resourcePk === null ||
        resourcePk === '' ||
        isTempUploadRow
      ) {
        openFromMergedDetail(null)
        return
      }

      const loading = this.$loading({
        lock: true,
        text: '加载文件信息…',
        background: 'rgba(0, 0, 0, 0.35)'
      })
      getCourseContentDetail(resourcePk)
        .then((raw) => {
          const detail = this.normalizeContentDetailPayload(raw)
          openFromMergedDetail(detail)
        })
        .catch(() => {
          openFromMergedDetail(null)
        })
        .finally(() => {
          loading.close()
        })
    },
    /** 兼容接口直接返回扁平 JSON 或 { code, result } */
    normalizeContentDetailPayload(raw) {
      if (!raw || typeof raw !== 'object') {
        return null
      }
      const inner = raw.result
      if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
        return inner
      }
      return raw
    },
    /** 下载文件名补全后缀，与列表标题一致 */
    ensureDownloadFilename(displayName, filePath) {
      let name = (displayName || 'download').trim()
      const fp = (filePath || '').split('?')[0]
      const dot = fp.lastIndexOf('.')
      const ext = dot >= 0 ? fp.slice(dot + 1).toLowerCase() : ''
      if (ext && name && !name.toLowerCase().endsWith(`.${ext}`)) {
        name = `${name}.${ext}`
      }
      return name || `download.${ext || 'bin'}`
    },
    /** 文档全屏顶栏下载：GET /obs/downloadToken 后 ulearning-obs 下载 */
    onDocumentFullscreenDownloadClick () {
      const pathKey = extractObsRelativePath(this.currentDocumentPreviewResource.filePath)
      const fullUrl = this.currentDocumentOriginalPath
      const saveName = this.ensureDownloadFilename(
        this.currentDocumentPreviewResource.fileName,
        this.currentDocumentPreviewResource.filePath
      )
      if (!pathKey || !fullUrl) {
        this.$message({ message: '无法下载：缺少文件存储路径。', type: 'warning' })
        return
      }
      const obs = new Obs(setObsConfig({
        uptokenHost: API_HOST,
        authorization: this.token,
        obsHost: RESOURCE_SERVER_HOST
      }))
      const loading = this.$loading({
        lock: true,
        text: '正在准备下载…',
        background: 'rgba(0, 0, 0, 0.35)'
      })
      getObsDownloadToken(pathKey)
        .then(() => {
          obs.download(fullUrl, saveName)
        })
        .catch((reason) => {
          const msg =
            reason && typeof reason.message === 'string'
              ? reason.message
              : '下载凭证获取失败，请稍后重试。'
          this.$message({ message: msg, type: 'error' })
        })
        .finally(() => {
          loading.close()
        })
    },
    isEmbeddedDocument(item) {
      const name = item.fileUrl || item.resourceName || ''
      const ext = this.getFileExt(name)
      return ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(ext)
    },
    isDocumentResource(path) {
      return ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(this.getFileExt(path || ''))
    },
    getFileExt(path) {
      const fileName = path.split('?')[0].toLowerCase()
      return fileName.substring(fileName.lastIndexOf('.') + 1)
    },
    enterFolder(item) {
      this.params.parentId = item.id
      this.params.pn = 1
      const index = this.folderPath.findIndex(folder => folder.id === item.id)
      if (index > -1) {
        this.folderPath = this.folderPath.slice(0, index + 1)
      } else {
        this.folderPath.push({
          id: item.id,
          resourceName: item.resourceName
        })
      }
      this.getResourceList()
    },
    backToRoot() {
      this.params.parentId = 0
      this.params.pn = 1
      this.folderPath = []
      this.getResourceList()
    },
    openRenameDialog(item) {
      this.currentResource = item
      this.renameForm.resourceName = item.resourceName
      this.renameDuplicateNameError = ''
      this.renameDialogVisible = true
      this.$nextTick(() => {
        this.$refs.renameForm.clearValidate()
      })
    },
    /** 输入变化时清除重名提示，便于修正后重新提交 */
    onRenameResourceNameInput() {
      if (this.renameDuplicateNameError) {
        this.renameDuplicateNameError = ''
      }
    },
    submitRename() {
      this.renameDuplicateNameError = ''
      this.$refs.renameForm.validate((valid) => {
        if (!valid) {
          return
        }
        const newName = (this.renameForm.resourceName || '').trim()
        const currentId = this.currentResource.id
        // 同目录列表内重名：仅比较名称（trim），避免类型不一致导致未拦截仍走接口并弹出全局 toast
        const hasSameName = this.resourceList.some((item) => {
          return String(item.id) !== String(currentId) && (item.resourceName || '').trim() === newName
        })
        if (hasSameName) {
          this.renameDuplicateNameError = '该名称已存在，请更换名称后重试。'
          return
        }
        const data = {
          id: this.currentResource.id,
          name: this.renameForm.resourceName,
          ocid: this.currentResource.ocId || this.$route.params.id,
          parentId: this.currentResource.parentId || 0,
          share: this.currentResource.share === undefined ? 1 : this.currentResource.share,
          type: this.currentResource.type === undefined ? 0 : this.currentResource.type
        }
        // 文件夹重命名仍走班课原接口；文件重命名走实验同步接口以回写 AI 实验附件
        const renameRequest =
          this.currentResource.resourceType === 'folder'
            ? editCourseContent(data, { skipCourseApiErrorToast: true })
            : editExperimentSyncedCourseContent(data, { skipCourseApiErrorToast: true })
        renameRequest
          .then(() => {
            this.renameDialogVisible = false
            this.$message({
              message: '重命名成功',
              type: 'success'
            })
            this.getResourceList()
          })
          .catch((reason) => {
            const apiMsg = this.extractCourseApiErrorMessage(reason)
            if (apiMsg && this.isRenameDuplicateBackendMessage(apiMsg)) {
              this.renameDuplicateNameError = apiMsg
              return
            }
            if (apiMsg) {
              this.$message({ message: apiMsg, type: 'error' })
            }
          })
      })
    },
    /** 从 courseApi 的 reject 结果中取出 message（兼容 HTTP 错误体与 code!=1 的业务对象） */
    extractCourseApiErrorMessage(reason) {
      if (!reason) {
        return ''
      }
      if (reason.response && reason.response.data) {
        return reason.response.data.message || reason.response.data.msg || ''
      }
      if (typeof reason.message === 'string') {
        return reason.message
      }
      return ''
    },
    /** 后端返回的重命名重名类提示，改为弹窗内展示而非顶部 toast */
    isRenameDuplicateBackendMessage(message) {
      if (!message || typeof message !== 'string') {
        return false
      }
      return (
        message.includes('已存在') ||
        message.includes('重复') ||
        message.includes('同名')
      )
    },
    deleteResource(item) {
      const idsToDelete = this.normalizeContentIdsForDelete([item.id])
      if (idsToDelete.length === 0) {
        this.$message({ message: '该资源无法删除，请刷新列表后重试', type: 'warning' })
        return
      }
      const message = item.resourceType === 'folder'
        ? '确定删除该文件夹？文件夹内所有内容将一并删除，且无法恢复。'
        : '确定删除该文件？删除后无法恢复。'
      this.$confirm(message, '删除')
        .then(() => {
          return this.deleteExperimentSyncedResources(idsToDelete)
        })
        .then(() => {
          this.$message({ message: '删除成功', type: 'success' })
          this.getResourceList()
        })
        .catch((reason) => {
          if (reason === 'cancel' || reason === 'close') {
            return
          }
          const apiMsg = this.extractCourseApiErrorMessage(reason)
          if (apiMsg) {
            this.$message({ message: apiMsg, type: 'error' })
          }
        })
    }
  }
}
</script>

<style lang="scss" scoped>
/* 列宽与设计标注一致：创建人 120、创建时间 + 创建人 + 操作合计约 350（120+120+110），与筛选栏网格对齐上传按钮 */
$resource-checkbox-col-width: 32px;
$resource-creator-col-width: 120px;
$resource-time-col-width: 120px;
$resource-action-col-width: 110px;

/* 实验资源列表样式按课程主页 UI 稿：单行工具栏、卡片表格、表头底色与日期格式等 */
.course-resource {
  padding-top: 24px;
  /* 根目录无资源：顶栏搜索 + 上传，与产品空态稿一致 */
  .filters--root-empty {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    .input-search--root-empty {
      width: 200px;
    }
    /* scoped 无法作用在 ul-button 根节点上，必须用深度选择器 */
    ::v-deep .button-right.ul-button {
      margin-left: auto;
      flex-shrink: 0;
      box-sizing: border-box;
      line-height: 1;
      border-radius: $--border-radius-base;
    }
  }
  .resource-root-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 120px;
    text-align: center;
    font-size: 14px;
    color: #969696;
    img {
      width: 190px;
      margin-bottom: 16px;
    }
    p {
      margin: 0;
    }
  }
  .filters {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    .folder-path {
      display: flex;
      align-items: center;
      min-width: 0;
      color: #666;
      font-size: 14px;
      .back-link {
        cursor: pointer;
        display: flex;
        align-items: center;
        i {
          color: #CBCBD1;
          font-size: 14px;
          margin-right: 4px;
        }
        &:hover {
          color: $--color-primary;
        }
      }
      .divider {
        width: 1px;
        height: 14px;
        margin: 0 10px;
        background-color: #E3E3E9;
      }
      .breadcrumb-item {
        cursor: pointer;
        &:hover {
          color: $--color-primary;
        }
      }
      .breadcrumb-root {
        color: #666;
      }
      .breadcrumb-current {
        color: $--color-text-secondary;
      }
      .icon-xialacaozuo {
        color: #CBCBD1;
        display: inline-block;
        font-size: 12px;
        margin: 0 6px;
        transform: scale(0.7) rotate(-90deg);
      }
    }
  }
  /* 已勾选时占位原筛选行左侧：批量删除按钮 + 已选计数（与 .folder-filters 首列对齐） */
  .resource-bulk-bar {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    .bulk-delete-text {
      box-sizing: border-box;
      min-width: 60px;
      height: 32px;
      padding: 0 16px;
      border: 1px solid #DCDCE6;
      border-radius: 4px;
      background-color: #fff;
      cursor: pointer;
      font-size: 14px;
      line-height: 30px;
      color: #EA5947;
      font-family: inherit;
      &:hover {
        border-color: #EA5947;
        opacity: 1;
      }
    }
    .selected-count {
      margin-left: 12px;
      color: $--color-text-secondary;
      font-size: 14px;
      line-height: 20px;
    }
  }
  .folder-filters {
    display: grid;
    align-items: center;
    /* 与表格列宽一致：前区跨复选框+名称列放置搜索与类型筛选，后为创建人 / 创建时间占位 + 上传 */
    grid-template-columns:
      #{$resource-checkbox-col-width}
      minmax(0, 1fr)
      #{$resource-creator-col-width}
      #{$resource-time-col-width}
      #{$resource-action-col-width};
    margin-bottom: 20px;
    /* 横跨「复选框列 + 资源名称列」，左内边距与 .resource-cell 一致，使搜索框左缘与勾选区内容左缘对齐 */
    .folder-filters__cell--filters {
      grid-column: 1 / span 2;
      min-width: 0;
    }
    /* 占位列的水平 padding 与 .resource-cell 一致，避免纵向缝不齐 */
    .folder-filters__cell--creator-gap {
      padding: 0 10px;
    }
    .folder-filters__cell--time-gap {
      padding: 0 10px;
    }
    .folder-filters__cell--action {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding-left: 10px;
      padding-right: 10px;
      box-sizing: border-box;
    }
    /* 依次为：「全部类型」（左）、搜索框（右）；固定横向顺序，避免 RTL 或父级 flex 倒置 */
    .folder-filters-left {
      display: flex;
      flex-direction: row;
      direction: ltr;
      align-items: center;
      flex-shrink: 0;
      column-gap: 10px;
      min-width: 0;
      padding-left: 10px;
      padding-right: 10px;
      box-sizing: border-box;
    }
    .select-type {
      width: 150px;
      margin-right: 0;
    }
    .input-search {
      width: 200px;
    }
    .folder-filters__cell--action ::v-deep .button-right.ul-button {
      flex-shrink: 0;
      box-sizing: border-box;
      line-height: 1;
      border-radius: $--border-radius-base;
    }
  }
  .total {
    margin-bottom: 8px;
    color: $--color-text-secondary;
  }
  /* 数据列表：表头实线分隔 + 表体虚线行距，与 UI 稿一致 */
  .resource-table {
    color: $--color-text-base;
    font-size: $--font-size-base;
    background-color: $--color-white;
    border: none;
    border-radius: 0;
    overflow: visible;
    .resource-row {
      display: flex;
      box-sizing: border-box;
      /* 表体行间：浅灰虚线 */
      &:not(.resource-header) {
        border-bottom: 1px dotted #E8E8EE;
      }
      &:hover:not(.resource-header) {
        background-color: #FAFAFD;
      }
      &.is-row-selected {
        background-color: #FFF8F7;
      }
    }
    /* 表头行纵向居中 */
    .resource-row.resource-header {
      align-items: center;
      min-height: 50px;
    }
    /* 数据行：列等高拉伸后，勾选区与名称区内容均贴在单元格底边，使勾选框下边与 24×24 图标及文件名可视底部对齐 */
    .resource-row:not(.resource-header) {
      align-items: stretch;
      min-height: 56px;
      /* 创建人单行省略（列窄时不换行） */
      .creator-cell {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        color: #666;
        display: flex;
        align-items: flex-end;
      }
      /* 两行省略时整块名称区下沉到底，与勾选列内 flex-end 对齐同一条下边线 */
      .name-cell {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        min-height: 0;
      }
      .time-cell,
      .action-cell {
        display: flex;
        align-items: flex-end;
        justify-content: flex-start;
      }
      .time-cell {
        color: #999;
      }
      .name-cell .resource-name span {
        color: $--color-text-base;
      }
    }
    /* 表头约 50px 高（设计稿标注），浅色底便于与表体区分 */
    .resource-header {
      min-height: 50px;
      font-weight: 600;
      color: #333;
      /* 与数据行同色底，整块列表视觉统一 */
      background-color: $--color-white;
      border-bottom: 1px solid #E8E8EE;
      /* 表头：可点击触发 /course/content/order 的排序 */
      .resource-header-sort-trigger {
        display: inline-flex;
        align-items: center;
        flex-wrap: wrap;
        min-width: 0;
        cursor: pointer;
        user-select: none;
      }
      /* 设计稿：排序箭头整体占位 12×12，与表头文案垂直居中 */
      .sort-pair {
        position: relative;
        display: inline-block;
        flex-shrink: 0;
        width: 12px;
        height: 12px;
        margin-left: 5px;
        vertical-align: middle;
        &::before,
        &::after {
          position: absolute;
          left: 50%;
          width: 0;
          height: 0;
          content: '';
          transform: translateX(-50%);
          border-right: 4px solid transparent;
          border-left: 4px solid transparent;
        }
        &::before {
          top: 1px;
          border-bottom: 5px solid #CBCBD1;
          border-top: none;
        }
        &::after {
          bottom: 1px;
          border-top: 5px solid #CBCBD1;
          border-bottom: none;
        }
        /* desc=0：升序，强调上三角；desc=1：降序，强调下三角 */
        &.sort-pair--asc::before {
          border-bottom-color: #737384;
        }
        &.sort-pair--desc::after {
          border-top-color: #737384;
        }
      }
    }
    .resource-cell {
      box-sizing: border-box;
      /* 设计稿标注：单元格纵向 15px，左右内边距 10px */
      padding: 15px 10px;
    }
    .checkbox-cell {
      width: $resource-checkbox-col-width;
      text-align: center;
      display: flex;
      align-items: flex-end;
      justify-content: center;
      /* 列表数据行勾选框与设计稿一致 14×14 */
      input {
        cursor: pointer;
        width: 14px;
        height: 14px;
        min-width: 14px;
        min-height: 14px;
        flex-shrink: 0;
        margin: 0;
        accent-color: $--color-primary;
      }
    }
    /* 表头全选勾选框与设计稿体积一致（非数据行不适用底对齐留白） */
    .resource-header .checkbox-cell {
      align-items: center;
    }
    .name-cell {
      flex: 1;
      min-width: 0;
      .total-text {
        margin-left: 8px;
        font-size: $--font-size-small;
        font-weight: 400;
        color: $--color-text-secondary;
      }
    }
    .creator-cell {
      width: $resource-creator-col-width;
      min-width: 0;
      box-sizing: border-box;
      /* 标题与正文均左对齐，与资源名称列一致 */
      text-align: left;
    }
    .time-cell {
      width: $resource-time-col-width;
      min-width: 0;
      box-sizing: border-box;
      text-align: left;
    }
    .action-cell {
      width: $resource-action-col-width;
      min-width: 0;
      box-sizing: border-box;
      text-align: left;
      /* 稿面：圆角 + 细灰边的操作按钮，文案与箭头同主色 */
      ::v-deep .ul-button.ul-dropdown-link {
        color: $--color-primary;
        background-color: $--color-white;
        border: 1px solid $--border-color-base;
        border-radius: $--border-radius-base;
        padding: 6px 10px;
        height: auto;
        min-height: 36px;
        line-height: 1.35;
        &:hover,
        &:focus {
          color: $--color-primary;
          border-color: mix($--color-primary, $--border-color-base, 35%);
          background-color: #FFFBFA;
        }
      }
      ::v-deep .ul-button.ul-dropdown-link .button-text {
        line-height: 18px;
        display: inline-block;
        margin-right: 10px;
        text-align: left;
      }
      ::v-deep .ul-button.ul-dropdown-link .icon-xialacaozuo {
        color: $--color-primary;
        display: inline-block;
        font-size: 12px;
        transform: scale(0.7);
      }
    }
  }
  /* 列表名称区：图标 24×24，与勾选框一起做底对齐（标题字号沿用表格正文字体） */
  .resource-name {
    display: flex;
    align-items: flex-end;
    cursor: pointer;
    .resource-icon {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      font-size: 24px;
      line-height: 24px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-right: 9px;
      margin-bottom: 0;
      &.resource-icon-folder {
        color: #F7B13D;
      }
      &.resource-icon-pdf {
        color: #F56C6C;
      }
      &.resource-icon-excel {
        color: #67C23A;
      }
      &.resource-icon-word {
        color: #409EFF;
      }
      &.resource-icon-ppt {
        color: #E6A23C;
      }
      &.resource-icon-video {
        color: #F7B13D;
      }
      &.resource-icon-other {
        color: #CBCBD1;
      }
    }
    span {
      flex: 1;
      min-width: 0;
      word-break: break-word;
      /* 文件名过长最多两行省略；底对齐时与图标/勾选框下边线齐平 */
      @include ellipsis2;
    }
  }
  .pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  }
  .resource-tips {
    color: #D0021B;
    font-size: 14px;
    line-height: 1.6;
    margin-top: 24px;
    .tips-title {
      font-weight: 600;
      margin-top: 18px;
    }
    p {
      margin: 0 0 8px;
    }
  }
  /* 重命名弹窗：名称已存在时输入框红色边框 + 下方小号红字提示（须写在 .course-resource 下，勿放进全屏预览容器） */
  .rename-form-item--name-taken {
    ::v-deep .ul-input__inner {
      border-color: #f56c6c;
    }
  }
  .rename-form-item .rename-duplicate-msg {
    margin: 0;
    color: #f5222d !important;
    font-size: 11px !important;
    line-height: 1.4;
    padding-top: 4px;
  }
}
.resource-menu {
  width: 80px;
}
.document-preview-wrapper {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 3000;
  background-color: rgba(0, 0, 0, 0.8);
  .document-preview-header {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 3001;
    box-sizing: border-box;
    width: 100%;
    height: 54px;
    padding: 9px 16px;
    color: #fff;
    background-color: rgba(0, 0, 0, 0.86);
    h4 {
      float: left;
      margin: 0;
      font-size: 16px;
      line-height: 36px;
    }
    .document-preview-actions {
      float: right;
      display: flex;
      align-items: center;
      line-height: 36px;
      .preview-button {
        display: inline-block;
        box-sizing: border-box;
        min-width: 96px;
        height: 34px;
        margin-left: 14px;
        padding: 0 18px;
        color: #EA5947;
        font-size: 14px;
        line-height: 32px;
        text-align: center;
        cursor: pointer;
        background-color: #fff;
        border: 1px solid #DCDCE6;
        border-radius: 4px;
        outline: none;
        text-decoration: none;
      }
      button.preview-button {
        font-family: inherit;
      }
    }
  }
  .document-preview-body {
    box-sizing: border-box;
    height: 100%;
    /* 左右不留边距：文档 iframe 居中窄幅时，两侧透出外层半透明遮罩（与 PDF 预览观感一致） */
    padding: 74px 0 18px;
    text-align: center;
    background-color: transparent;
    iframe {
      display: block;
      box-sizing: border-box;
      width: 100%;
      max-width: 940px;
      height: calc(100vh - 92px);
      margin: 0 auto;
      background-color: #fff;
      border: none;
      /* 避免父级含 transform 的合成层时文档（尤其 Excel）下半区被误判为在遮罩下层 */
      position: relative;
      z-index: 2;
    }
  }
}
.mask-fade-enter-active {
  animation: mask-fade-in .3s;
}
.mask-fade-leave-active {
  animation: mask-fade-out .3s;
}
/* 仅用 opacity，避免对 .document-preview-wrapper 使用 transform 导致与内嵌文档（Excel）合成层错乱、下半区透出遮罩色 */
@keyframes mask-fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@keyframes mask-fade-out {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
/* 上传等待层（盖住整页，避免连续操作） */
.upload-wait-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}
.upload-wait-panel {
  box-sizing: border-box;
  min-width: 260px;
  padding: 28px 36px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  text-align: center;
}
.upload-wait-spinner {
  box-sizing: border-box;
  width: 40px;
  height: 40px;
  margin: 0 auto;
  border: 3px solid #f0f0f0;
  border-top-color: #EA5947;
  border-radius: 50%;
  animation: course-resource-upload-spin 0.75s linear infinite;
}
.upload-wait-text {
  margin: 18px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: #666;
}
.upload-wait-fade-enter-active,
.upload-wait-fade-leave-active {
  transition: opacity 0.2s ease;
}
.upload-wait-fade-enter,
.upload-wait-fade-leave-to {
  opacity: 0;
}
@keyframes course-resource-upload-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
